import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
  FiArrowRight, FiArrowUpRight, FiBarChart2, FiBookOpen, FiBriefcase, FiCheck, FiChevronDown,
  FiCpu, FiDollarSign, FiEye, FiFileText, FiGlobe, FiGrid, FiHelpCircle, FiLayers,
  FiLinkedin, FiLock, FiMenu, FiMessageSquare, FiPackage, FiPenTool, FiSend, FiServer, FiShield,
  FiTool, FiUploadCloud, FiUserCheck, FiUsers, FiX, FiZap,
} from 'react-icons/fi';
import logo from './assets/Novalink Solution full -R.png';
import ThemeToggle from '../components/ThemeToggle.jsx';
import { useReveal } from '../utils/useReveal';
import {
  navLinks, trustItems, problems, steps, capabilities, reasons, useCases, plans, comparison,
  faqs, demoChat, suggestions,
} from './content.js';
import './LandingPage.css';

const capabilityIcons = {
  training: FiZap, vision: FiEye, crawl: FiGlobe, tenant: FiLayers, rbac: FiUserCheck,
  suggest: FiHelpCircle, brand: FiPenTool, security: FiShield, users: FiUsers,
};

const useCaseIcons = {
  hr: FiUsers, compliance: FiShield, finance: FiDollarSign, it: FiTool, training: FiBookOpen,
  ops: FiGrid, legal: FiBriefcase, product: FiPackage, intranet: FiServer, web: FiGlobe,
};

const stepIcons = [FiUploadCloud, FiCpu, FiMessageSquare, FiFileText];

// Feeds the cursor position to CSS for the card spotlight hover effect.
function trackPointer(e) {
  const r = e.currentTarget.getBoundingClientRect();
  e.currentTarget.style.setProperty('--mx', `${e.clientX - r.left}px`);
  e.currentTarget.style.setProperty('--my', `${e.clientY - r.top}px`);
}

function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const close = () => setOpen(false);

  return (
    <header className={`nb-header ${scrolled ? 'is-scrolled' : ''}`}>
      <div className="nb-wrap nb-header-inner">
        <a href="#top" className="nb-logo" aria-label="NovaBotX home">
          <img src={logo} alt="Novalink Solutions" />
          <span className="nb-logo-divider" aria-hidden="true" />
          <span className="nb-logo-product">NovaBotX<sup>™</sup></span>
        </a>

        <nav className={`nb-nav ${open ? 'is-open' : ''}`} aria-label="Primary">
          {navLinks.map((l) => (
            <a key={l.href} href={l.href} onClick={close}>{l.label}</a>
          ))}
          <div className="nb-nav-actions">
            <Link to="/login" className="nb-btn nb-btn-ghost" onClick={close}>Log in</Link>
            <Link to="/register" className="nb-btn nb-btn-primary" onClick={close}>Create account</Link>
          </div>
        </nav>

        <button
          type="button"
          className="nb-menu-toggle"
          aria-label={open ? 'Close menu' : 'Open menu'}
          aria-expanded={open}
          onClick={() => setOpen(!open)}
        >
          {open ? <FiX /> : <FiMenu />}
        </button>
        <ThemeToggle className="nb-header-theme" />
      </div>
      <span className="nb-scroll-progress" aria-hidden="true" />
    </header>
  );
}

// Plays the scripted conversation one message at a time, then loops.
function ChatPreview() {
  const [shown, setShown] = useState(0);
  const [typing, setTyping] = useState(false);

  useEffect(() => {
    if (shown >= demoChat.length) {
      const reset = setTimeout(() => setShown(0), 5000);
      return () => clearTimeout(reset);
    }
    const next = demoChat[shown];
    const isBot = next.from === 'bot';
    setTyping(isBot);
    const t = setTimeout(() => { setTyping(false); setShown(shown + 1); }, isBot ? 1500 : shown === 0 ? 600 : 1300);
    return () => clearTimeout(t);
  }, [shown]);

  return (
    <div className="nb-chat">
      <div className="nb-chat-head">
        <div className="nb-chat-avatar"><FiCpu /></div>
        <div>
          <div className="nb-chat-name">NovaBotX™ Assistant</div>
          <div className="nb-chat-status"><span className="nb-dot" /> Trained on 248 documents · 12 sites</div>
        </div>
      </div>

      <div className="nb-chat-body" aria-live="polite">
        {demoChat.slice(0, shown).map((m, i) => (
          <div key={i} className={`nb-msg nb-msg-${m.from}`}>
            <p>{m.text}</p>
            {m.source && (
              <span className="nb-source"><FiFileText /> {m.source}</span>
            )}
          </div>
        ))}
        {typing && (
          <div className="nb-msg nb-msg-bot nb-typing" aria-label="NovaBotX is typing">
            <span /><span /><span />
          </div>
        )}
      </div>

      <div className="nb-chat-suggest">
        {suggestions.map((s) => <span key={s} className="nb-chip">{s}</span>)}
      </div>
      <div className="nb-chat-input">
        <span>Ask about policies, SOPs, products…</span>
        <span className="nb-chat-send"><FiSend /></span>
      </div>
    </div>
  );
}

function Hero() {
  return (
    <section className="nb-hero" id="top">
      <div className="nb-hero-bg" aria-hidden="true" />
      <div className="nb-wrap nb-hero-grid">
        <div className="nb-hero-copy nb-stagger">
          <span className="nb-eyebrow"><span className="nb-dot" /> Enterprise AI knowledge assistant</span>
          <h1>
            Turn your documents and websites into <span className="nb-grad">instant, trusted answers.</span>
          </h1>
          <p className="nb-lead">
            NovaBotX™ transforms PDFs, images, text files, links and web content into a secure,
            conversational AI assistant — with source-aware answers and enterprise-grade security.
          </p>
          <div className="nb-hero-ctas">
            <a href="#contact" className="nb-btn nb-btn-primary nb-btn-lg">Request a demo <FiArrowUpRight /></a>
            <Link to="/register" className="nb-btn nb-btn-outline nb-btn-lg">Start free trial</Link>
          </div>
          <p className="nb-hero-signin">Already a customer? <Link to="/login">Sign in <FiArrowRight /></Link></p>
        </div>
        <ChatPreview />
      </div>

      <div className="nb-wrap">
        <ul className="nb-trust">
          {trustItems.map((t) => <li key={t}><FiCheck /> {t}</li>)}
        </ul>
      </div>
    </section>
  );
}

function SectionHead({ eyebrow, title, sub, center }) {
  return (
    <div className={`nb-section-head ${center ? 'is-center' : ''}`} data-reveal>
      <span className="nb-kicker">{eyebrow}</span>
      <h2>{title}</h2>
      {sub && <p>{sub}</p>}
    </div>
  );
}

function Problem() {
  return (
    <section className="nb-section" id="solutions">
      <div className="nb-wrap nb-problem">
        <div>
          <SectionHead
            eyebrow="The problem"
            title={<>Information is everywhere.<br /><span className="nb-muted">Answers are not.</span></>}
            sub="Enterprises store critical knowledge across PDFs, SOPs, HR policies, finance documents, intranets and websites. Traditional search and chatbots can’t understand complex documents, tables, images, or live web content."
          />
          <ul className="nb-problem-list">
            {problems.map((p, i) => <li key={p} data-reveal="left" style={{ '--reveal-i': i }}><FiX /> {p}</li>)}
          </ul>
        </div>

        <div className="nb-solution-card" data-reveal="right">
          <span className="nb-kicker nb-kicker-light">The solution</span>
          <h3>Meet NovaBotX™ — your enterprise knowledge brain</h3>
          <p>
            One conversational assistant that understands text, images, tables, diagrams and live
            web pages. Ask in natural language. Get instant, source-aware answers — securely.
          </p>
          <div className="nb-flow">
            <div className="nb-flow-node">
              <div className="nb-flow-icons"><FiFileText /><FiGlobe /></div>
              <span>Documents &amp; websites</span>
            </div>
            <div className="nb-flow-line" aria-hidden="true"><span /></div>
            <div className="nb-flow-node is-core">
              <div className="nb-flow-icons"><FiCpu /></div>
              <span>NovaBotX™</span>
            </div>
            <div className="nb-flow-line" aria-hidden="true"><span /></div>
            <div className="nb-flow-node">
              <div className="nb-flow-icons"><FiZap /></div>
              <span>Instant answers</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Workflow() {
  return (
    <section className="nb-section nb-section-tint" id="workflow">
      <div className="nb-wrap">
        <SectionHead
          center
          eyebrow="How it works"
          title="From scattered files to a trained assistant in minutes"
          sub="No coding or AI expertise required — built for business users and administrators."
        />
        <ol className="nb-steps">
          {steps.map((s, i) => {
            const Icon = stepIcons[i];
            return (
              <li key={s.title} className="nb-step" data-reveal style={{ '--reveal-i': i }}>
                <div className="nb-step-top">
                  <span className="nb-step-icon"><Icon /></span>
                  <span className="nb-step-num">0{i + 1}</span>
                </div>
                <h3>{s.title}</h3>
                <p>{s.text}</p>
              </li>
            );
          })}
        </ol>
      </div>
    </section>
  );
}

function Capabilities() {
  return (
    <section className="nb-section" id="features">
      <div className="nb-wrap">
        <SectionHead
          eyebrow="Capabilities"
          title="Enterprise-grade from day one"
          sub="Everything you need to deploy a governed, secure knowledge assistant across your organization."
        />
        <div className="nb-cap-grid">
          {capabilities.map((c, i) => {
            const Icon = capabilityIcons[c.key];
            return (
              <article key={c.key} className={`nb-cap ${i === 0 || i === 1 ? 'is-wide' : ''}`} data-reveal style={{ '--reveal-i': i % 4 }} onMouseMove={trackPointer}>
                <span className="nb-cap-icon"><Icon /></span>
                <h3>{c.title}</h3>
                <p>{c.text}</p>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Why() {
  return (
    <section className="nb-section nb-why" id="whynovabot">
      <div className="nb-wrap nb-why-grid">
        <div>
          <SectionHead eyebrow="Why NovaBotX™" title="Why enterprises choose NovaBotX™" />
          <ul className="nb-why-list">
            {reasons.map((r, i) => <li key={r} data-reveal="left" style={{ '--reveal-i': i }}><span><FiCheck /></span>{r}</li>)}
          </ul>
        </div>
        <div className="nb-why-visual" aria-hidden="true" data-reveal="zoom">
          <div className="nb-orbit nb-orbit-1" />
          <div className="nb-orbit nb-orbit-2" />
          <div className="nb-why-core"><FiCpu /></div>
          {[FiFileText, FiGlobe, FiBarChart2, FiLock, FiUsers, FiMessageSquare].map((Icon, i) => (
            <span key={i} className="nb-sat" style={{ '--i': i }}><Icon /></span>
          ))}
        </div>
      </div>
    </section>
  );
}

function UseCases() {
  return (
    <section className="nb-section nb-section-tint" id="usecases">
      <div className="nb-wrap">
        <SectionHead center eyebrow="Use cases" title="Built for every knowledge-driven team" />
        <div className="nb-uc-grid">
          {useCases.map((u, i) => {
            const Icon = useCaseIcons[u.key];
            return (
              <article key={u.key} className="nb-uc" data-reveal style={{ '--reveal-i': i % 5 }} onMouseMove={trackPointer}>
                <span className="nb-uc-icon"><Icon /></span>
                <div>
                  <h3>{u.title}</h3>
                  <p>{u.text}</p>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Pricing() {
  const cell = (v) => {
    if (v === true) return <FiCheck className="nb-yes" aria-label="Included" />;
    if (v === false) return <FiX className="nb-no" aria-label="Not included" />;
    return v;
  };

  return (
    <section className="nb-section" id="pricing">
      <div className="nb-wrap">
        <SectionHead center eyebrow="Pricing" title="Flexible plans for teams and enterprises" sub="Start small, scale across departments. Every plan includes vision AI and source-aware answers." />
        <div className="nb-plans">
          {plans.map((p, i) => (
            <article key={p.name} className={`nb-plan ${p.featured ? 'is-featured' : ''}`} data-reveal style={{ '--reveal-i': i }}>
              {p.featured && <span className="nb-plan-badge">Most popular</span>}
              <h3>{p.name}</h3>
              <p className="nb-plan-desc">{p.desc}</p>
              <div className="nb-plan-price">
                {p.price}{p.period && <span>{p.period}</span>}
              </div>
              <p className="nb-plan-annual">{p.annual || ' '}</p>
              {p.cta.to ? (
                <Link to={p.cta.to} className={`nb-btn nb-btn-block ${p.featured ? 'nb-btn-primary' : 'nb-btn-outline'}`}>{p.cta.label}</Link>
              ) : (
                <a href={p.cta.href} className="nb-btn nb-btn-block nb-btn-outline">{p.cta.label}</a>
              )}
              <ul>
                {p.features.map((f) => <li key={f}><FiCheck /> {f}</li>)}
              </ul>
              <p className="nb-plan-ideal"><strong>Ideal for</strong> {p.ideal}</p>
            </article>
          ))}
        </div>

        <div className="nb-compare" data-reveal>
          <h3>Feature comparison</h3>
          <div className="nb-table-scroll">
            <table>
              <thead>
                <tr><th>Feature</th><th>Starter</th><th>Business</th><th>Enterprise</th></tr>
              </thead>
              <tbody>
                {comparison.map(([label, ...vals]) => (
                  <tr key={label}>
                    <td>{label}</td>
                    {vals.map((v, i) => <td key={i}>{cell(v)}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
}

function FAQ() {
  const [openIndex, setOpenIndex] = useState(0);
  const [visible, setVisible] = useState(6);

  return (
    <section className="nb-section nb-section-tint" id="faq">
      <div className="nb-wrap nb-faq-grid">
        <SectionHead
          eyebrow="FAQ"
          title="Frequently asked questions"
          sub={<>Can’t find what you’re looking for? <a href="#contact">Talk to our team</a>.</>}
        />
        <div>
          <div className="nb-faq">
            {faqs.slice(0, visible).map((f, i) => {
              const open = openIndex === i;
              return (
                <div key={f.q} className={`nb-faq-item ${open ? 'is-open' : ''}`}>
                  <button type="button" aria-expanded={open} onClick={() => setOpenIndex(open ? null : i)}>
                    <span>{f.q}</span>
                    <FiChevronDown />
                  </button>
                  {open && <div className="nb-faq-answer">{f.a}</div>}
                </div>
              );
            })}
          </div>
          <div className="nb-faq-more">
            {visible < faqs.length && (
              <button type="button" className="nb-btn nb-btn-outline" onClick={() => setVisible(Math.min(visible + 6, faqs.length))}>
                Show more questions
              </button>
            )}
            {visible > 6 && (
              <button type="button" className="nb-btn nb-btn-ghost" onClick={() => { setVisible(6); setOpenIndex(null); }}>
                Show less
              </button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function Contact() {
  const { register, handleSubmit, control, reset, formState: { errors } } = useForm();
  const [sent, setSent] = useState(false);

  const onSubmit = (data) => {
    console.log('Form Data:', data);
    setSent(true);
    reset();
  };

  // Only future dates (tomorrow onwards)
  const minDate = new Date();
  minDate.setDate(minDate.getDate() + 1);

  return (
    <section className="nb-section" id="contact">
      <div className="nb-wrap">
        <div className="nb-contact" data-reveal="zoom">
          <div className="nb-contact-copy">
            <span className="nb-kicker nb-kicker-light">Book a meeting</span>
            <h2>See NovaBotX™ with your own documents</h2>
            <p>Tell us a little about your team and pick a date. We’ll set up a live demo trained on your content.</p>
            <ul>
              <li><FiCheck /> Live walkthrough with your files and links</li>
              <li><FiCheck /> Security &amp; tenant isolation review</li>
              <li><FiCheck /> Plan recommendation for your team size</li>
            </ul>
          </div>

          <form className="nb-form" onSubmit={handleSubmit(onSubmit)} noValidate>
            {sent && <div className="nb-form-success"><FiCheck /> Thanks! We’ll be in touch shortly.</div>}
            <div className="nb-field-row">
              <label className="nb-field">
                <span>Name</span>
                <input type="text" placeholder="Jane Doe" {...register('name', { required: 'Name is required' })} />
                {errors.name && <em>{errors.name.message}</em>}
              </label>
              <label className="nb-field">
                <span>Work email</span>
                <input
                  type="email"
                  placeholder="jane@company.com"
                  {...register('email', {
                    required: 'Email is required',
                    pattern: { value: /^\S+@\S+\.\S+$/, message: 'Invalid email address' },
                  })}
                />
                {errors.email && <em>{errors.email.message}</em>}
              </label>
            </div>
            <div className="nb-field-row">
              <label className="nb-field">
                <span>Company</span>
                <input type="text" placeholder="Company name" {...register('company', { required: 'Company name is required' })} />
                {errors.company && <em>{errors.company.message}</em>}
              </label>
              <div className="nb-field">
                <span>Preferred date</span>
                <Controller
                  control={control}
                  name="meetingDate"
                  rules={{ required: 'Please select a date' }}
                  render={({ field }) => (
                    <DatePicker
                      placeholderText="Select a date"
                      selected={field.value}
                      onChange={field.onChange}
                      minDate={minDate}
                      className="nb-date"
                    />
                  )}
                />
                {errors.meetingDate && <em>{errors.meetingDate.message}</em>}
              </div>
            </div>
            <label className="nb-field">
              <span>Message</span>
              <textarea rows="4" placeholder="What would you like NovaBotX™ to answer?" {...register('message', { required: 'Message is required' })} />
              {errors.message && <em>{errors.message.message}</em>}
            </label>
            <button type="submit" className="nb-btn nb-btn-primary nb-btn-lg nb-btn-block">Request my demo <FiArrowRight /></button>
          </form>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="nb-footer">
      <div className="nb-wrap nb-footer-grid">
        <div className="nb-footer-brand">
          <img src={logo} alt="Novalink Solutions" />
          <p>Enterprise AI knowledge assistant that turns documents, websites, and links into instant, accurate answers.</p>
          <a className="nb-social" href="https://www.linkedin.com/company/novalink-solutions-llc/posts/?feedView=all" aria-label="LinkedIn"><FiLinkedin /></a>
        </div>
        <div>
          <h4>Product</h4>
          <ul>
            <li><a href="#features">Features</a></li>
            <li><a href="#workflow">How it works</a></li>
            <li><a href="#whynovabot">Why NovaBotX™</a></li>
            <li><a href="#pricing">Pricing</a></li>
          </ul>
        </div>
        <div>
          <h4>Resources</h4>
          <ul>
            <li><a href="#usecases">Use cases</a></li>
            <li><a href="#faq">FAQ</a></li>
            <li><a href="/privacy-policy">Privacy policy</a></li>
            <li><a href="/terms-and-conditions">Terms &amp; conditions</a></li>
          </ul>
        </div>
        <div>
          <h4>Account</h4>
          <ul>
            <li><Link to="/login">Log in</Link></li>
            <li><Link to="/register">Create account</Link></li>
            <li><a href="#contact">Request a demo</a></li>
          </ul>
        </div>
      </div>
      <div className="nb-wrap nb-footer-bottom">© {new Date().getFullYear()} NovaBotX™ by Novalink Solutions. All rights reserved.</div>
    </footer>
  );
}

const LandingPage = () => {
  useReveal();

  return (
    <div className="nb-landing">
      <Header />
      <main>
        <Hero />
        <Problem />
        <Workflow />
        <Capabilities />
        <Why />
        <UseCases />
        <Pricing />
        <FAQ />
        <Contact />
      </main>
      <Footer />
    </div>
  );
};

export default LandingPage;
